import { motion } from "framer-motion";
import { Award, Globe, Users, TrendingUp } from "lucide-react";

const stats = [
  { icon: Globe, value: "4", label: "Countries" },
  { icon: Award, value: "€2B+", label: "Portfolio Value" },
  { icon: Users, value: "150+", label: "Clients Served" },
  { icon: TrendingUp, value: "15%", label: "Avg. ROI" },
];

export const AboutSection = () => {
  return (
    <section id="about" className="py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.4em] text-gold/80 mb-4">
              Est. Three Generations
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-8 leading-tight">
              Noble Legacy.
              <span className="block italic font-medium">Global Destiny.</span>
            </h2>
            
            <div className="space-y-6 font-body text-muted-foreground leading-relaxed">
              <p className="text-lg text-foreground/80 font-medium">
                We serve those who seek more than property — those who build legacies.
              </p>
              <p>
                We are the <span className="text-foreground font-medium">sixth generation of builders</span> in 
                the Viršilas family, with three generations currently working together. Our roots trace back 
                to the same Lithuanian town where billionaire investor Mark Cuban's grandmother grew up — 
                our families faced life's challenges as neighbors, building a legacy of resilience and ambition.
              </p>
              <p>
                With nearly three decades bridging international markets, 
                we identify properties of true distinction — rare estates 
                that become enduring investments across generations.
              </p>
              <p>
                From Italy's timeless coastal estates to Monaco's exclusive harborfronts, 
                Spain's sun-drenched villas, and Dubai's architectural masterpieces — 
                our counsel is refined through generations of expertise.
              </p>
            </div>

            {/* Signature Tag */}
            <div className="mt-10 pt-8 border-t border-border">
              <p className="font-display text-lg text-foreground/90 italic mb-2">
                "True wealth is measured in legacy."
              </p>
              <p className="font-body text-sm uppercase tracking-[0.15em] text-muted-foreground">
                — The Viršilas Family
              </p>
            </div>
          </motion.div>

          {/* Right: Stats */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 gap-6 lg:gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="bg-card p-8 lg:p-10 shadow-subtle hover:shadow-elevated transition-shadow duration-300"
                >
                  <stat.icon className="w-8 h-8 text-gold mb-6" strokeWidth={1.5} />
                  <p className="font-display text-4xl lg:text-5xl text-foreground mb-2">
                    {stat.value}
                  </p>
                  <p className="font-body text-sm uppercase tracking-[0.1em] text-muted-foreground">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
